import React, { useEffect, useRef, useState } from 'react'
import Image from "next/legacy/image"
import AddToCartButton from '@components/ui/AddToCartButton'
import s from './FeatureTeaser.module.css'
import { useTranslations } from 'next-intl'
import { urlFor } from '@lib/sanity'
import { useRouter } from 'next/router'
import { parseGidWithParams } from '@shopify/admin-graphql-api-utilities'


export interface IFeatureTeaserItem {
  recommendation?: boolean
  bestseller?: boolean
  blood?: boolean
  urine?: boolean
  url: string
  teaserImage: any
  title: string
  price: string
  description: string
  productId: string
  variantId: string
  features: { icon: string, description: string }[]
  parameters: { title: string, description: string }[]
}

export interface FeatureTeaserItemProps {
  className?: string
  featureTeaserItem: IFeatureTeaserItem
}

const FeatureTeaserItem: React.FC<FeatureTeaserItemProps> = ((props) => {
  let {
    featureTeaserItem
  } = props

  const t = useTranslations('Cart')
  const { replace, reload } = useRouter()
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false)
  const [tooltipTitle, setTooltipTitle] = useState<string | undefined | null>(undefined)
  const [tooltipDescription, setTooltipDescription] = useState<string | undefined | null>(undefined)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const plainProductId = parseGidWithParams(featureTeaserItem?.productId)?.id

  const onLearnMoreButtonClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    if (featureTeaserItem?.url) {
      replace(featureTeaserItem?.url).then(() => reload())
    }
  }

  const showParameterTooltip = (event: React.MouseEvent<HTMLSpanElement>, title: string, description: string) => {
    event.preventDefault()

    setTooltipTitle(title)
    setTooltipDescription(description)

    const eventTarget = event.target as HTMLSpanElement
    if (eventTarget != null && wrapperRef?.current != null && tooltipRef != null) {
      const wrapperRect = wrapperRef.current?.getBoundingClientRect()
      const targetRect = eventTarget.getBoundingClientRect()

      if (wrapperRect != null && targetRect != null) {
        const x = targetRect.right - wrapperRect.left
        const y = targetRect.top - wrapperRect.top

        tooltipRef.current!.style.left = `${x}px`
        tooltipRef.current!.style.top = `${y}px`
      }
    }

    setTooltipVisible(true)
  }

  useEffect(() => {
    document.addEventListener('click', (ev: MouseEvent) => {
      const eventTarget = ev.target as HTMLElement
      if (!eventTarget?.classList?.contains(`info-button-${plainProductId}`)) {
        setTooltipTitle(undefined)
        setTooltipDescription(undefined)
        setTooltipVisible(false)
      }
    })
  })


  return (
    <div className='w-full h-full bg-neutral50 shadow-shadowXS relative pb-8'>
      <div
        className={`${featureTeaserItem?.bestseller === true ? 'bg-primary400' : (featureTeaserItem?.recommendation === true ? 'bg-primary100' : '')} uppercase p-3 text-center text-neutral900 font-medium text-paragraph03`}>
        {featureTeaserItem?.recommendation === true && featureTeaserItem?.bestseller !== true &&
          <span>{t('recommendation')}</span>}
        {featureTeaserItem?.bestseller === true && <span>{t('bestseller')}</span>}
        {featureTeaserItem?.bestseller !== true && featureTeaserItem?.recommendation !== true && <span>&nbsp;</span>}
      </div>

      <div className='relative h-28 w-full rounded-b-3xl overflow-hidden'>
        <Image
          className={'w-full'}
          alt='Featured product image'
          src={urlFor(featureTeaserItem?.teaserImage).url() ?? 'about:blank'}
          layout='fill'
          objectFit='cover'
          priority={false} loading="lazy"/>

        <div className='absolute top-0 inset-x-0 flex justify-end'>
          {featureTeaserItem?.urine === true && <span
            className='bg-neutral50 text-neutral900 rounded-cornerS inline-block px-space3 py-space1 text-paragraph03 mr-space3 mt-space3 cursor-default'>{t('urine')}</span>}
          {featureTeaserItem?.blood === true && <span
            className='bg-tertiary200 text-neutral50 rounded-cornerS inline-block px-space3 py-space1 text-paragraph03 mr-space3 mt-space3 cursor-default'>{t('blood')}</span>}
        </div>
      </div>

        <div className='mt-4 flex flex-col items-center px-4'>
          <h4 className='text-neutral900 font-medium text-paragraph01 mb-3'>{featureTeaserItem?.title}</h4>
          <h1 className='text-neutral900 text-heading02 mb-3'>{featureTeaserItem?.price}</h1>
          <p className='text-center text-paragraph03 text-neutral700 mb-6'>{featureTeaserItem?.description}</p>
          <div className='mb-3'>
            <AddToCartButton
              styleVariant='secondaryXSmall'
              active={true}
              amount={1}
              productId={featureTeaserItem.productId}
              variantId={featureTeaserItem.variantId}
              labelAtc={t('atc')} />
          </div>
          <span className='text-tertiary500 mb-8'>
            <a
              className='cursor-pointer'
              onClick={onLearnMoreButtonClick}>{t('learnMore')} &gt;</a>
          </span>
        </div>

        <h3 className='uppercase text-neutral600 text-paragraph03 font-medium mb-4 px-4'>{t('whatYouGet')}</h3>
        <div className={`${s.featureGrid} grid gap-x-2 gap-y-4 mb-10 px-4`}>
          {
            featureTeaserItem?.features.map(
              (feature, i) => <React.Fragment key={i}>
                <span className={`text-icon22 ${feature.icon} self-center`}></span>
                <span className='text-paragraph03 text-neutral600 self-center'>{feature.description}</span>
              </React.Fragment>
            )
          }
        </div>


        <h3 className='uppercase text-neutral600 text-paragraph03 font-medium mb-2 px-4'>{t('whatsTested')}</h3>

        <div ref={tooltipRef}
             className={`${s.tooltip} ${tooltipVisible ? '' : 'hidden'} bg-neutral50 px-4 absolute shadow-shadowXS z-20 rounded-cornerXS p-6 w-72`}>
          <span className='block text-neutral900 uppercase font-medium text-paragraph02 mb-2'>{tooltipTitle}</span>
          <span className='block text-neutral700 text-paragraph03'>{tooltipDescription}</span>
        </div>


        {
          featureTeaserItem?.parameters.map(
            (parameter, i) =>
              <div key={i} className='flex items-center w-full px-4 border-b py-2'>
                <span className='text-neutral600 text-icon22 icon-22-Check-22 self-center'></span>
                <span className='flex-1 mx-2 text-paragraph03 text-neutral900 self-center'>{parameter.title}</span>
                <span
                  className={`info-button-${plainProductId} aspect-square text-neutral600 self-center h-4 w-4 border border-neutral600 rounded-full text-xs flex justify-center items-center font-thin cursor-pointer`}
                  onClick={(event) => showParameterTooltip(event, parameter.title, parameter.description)}>i</span>
              </div>
          )
        }
    </div>
  )
})

export default FeatureTeaserItem
